/**
 * iter-219 — Route-level feature gate.
 *
 * Wraps a protected route. If the current user's plan doesn't include
 * `feature`, we render an "Upgrade required" page instead of the content.
 * Backend `FeatureGateMiddleware` still returns 402 for API hits — this
 * is purely a nicer UX than a toast.
 */
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import { useFeatures } from '../contexts/FeaturesContext';
import { useAuth } from '../contexts/AuthContext';

const FEATURE_LABELS = {
  insurance: 'Insurance Module',
  lifeline: 'Lifeline Calendar',
  team_access: 'Team Access (CA / Spouse)',
};

export const FeatureGate = ({ feature, children }) => {
  const { hasFeature, loading } = useFeatures();
  const { user } = useAuth();
  if (loading) return null;             // optimistic — avoid flash
  if (!user) return <Navigate to="/login" replace />;
  if (hasFeature(feature)) return children;
  const label = FEATURE_LABELS[feature] || feature;
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#FAFAF7' }}>
      <div className="w-full max-w-md rounded-xl p-8 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E5DF' }} data-testid={`feature-gate-${feature}`}>
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ backgroundColor: '#F7F7F5' }}>
          <Lock className="h-6 w-6" style={{ color: '#1B4332' }} />
        </div>
        <h1 className="text-xl font-medium mb-2" style={{ color: '#1C1917', fontFamily: 'Work Sans' }}>
          {label} is not in your plan
        </h1>
        <p className="text-sm mb-5" style={{ color: '#57534E' }}>
          Your current subscription doesn't include this module. Upgrade from the Pricing page to unlock {label.toLowerCase()} alongside your existing portfolios.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-4 py-2 rounded"
          style={{ backgroundColor: '#1B4332', color: '#F7F7F5' }}
          data-testid={`upgrade-cta-${feature}`}
        >
          <Sparkles className="h-4 w-4" /> See pricing options
        </Link>
      </div>
    </div>
  );
};
